import { useState } from "react";
import { SidePanel } from "../SidePanel";

type SceneDockProps = React.ComponentProps<typeof SidePanel>;

/**
 * Right dock wrapping the Chat/People/Perks panel.
 * - Desktop (md+): inline, collapsible via an edge tab (reclaims canvas when hidden).
 * - Mobile (<md): a floating button opens the panel as a bottom sheet over the scene.
 */
export function SceneDock(props: SceneDockProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Desktop: inline collapsible column */}
      <div className="relative hidden md:flex">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Show panel" : "Hide panel"}
          aria-label={collapsed ? "Show panel" : "Hide panel"}
          className="absolute top-3 left-0 z-40 flex h-9 w-5 -translate-x-full items-center justify-center rounded-l-lg border border-r-0 border-[var(--vy-surface-border)] bg-[var(--vy-surface)] text-[var(--vy-surface-fg-3)] shadow-[var(--shadow-sm)] hover:text-[var(--vy-surface-fg)]"
        >
          <Chevron dir={collapsed ? "left" : "right"} />
        </button>
        <div
          className={`h-full overflow-hidden border-l border-[var(--vy-surface-border)] transition-[width] duration-200 ease-out ${
            collapsed ? "w-0 border-l-0" : "w-[300px]"
          }`}
        >
          <div className="h-full w-[300px]">
            <SidePanel {...props} />
          </div>
        </div>
      </div>

      {/* Mobile: floating opener + bottom sheet */}
      {!mobileOpen ? (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="absolute right-3 bottom-20 z-40 flex items-center gap-1.5 rounded-full border border-white/15 bg-[var(--vy-dock)] px-3.5 py-2 text-xs font-bold text-white shadow-lg backdrop-blur-sm md:hidden"
        >
          Chat & people
        </button>
      ) : null}
      {mobileOpen ? (
        <div className="absolute inset-x-0 bottom-0 z-40 flex h-[62vh] flex-col overflow-hidden rounded-t-2xl border-t border-[var(--vy-surface-border)] bg-[var(--vy-surface)] shadow-[var(--shadow-lg)] md:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="flex shrink-0 items-center justify-center py-2 text-[var(--vy-surface-fg-3)]"
            aria-label="Close panel"
          >
            <span className="h-1 w-10 rounded-full bg-[var(--vy-surface-border)]" />
          </button>
          <div className="min-h-0 flex-1">
            <SidePanel {...props} />
          </div>
        </div>
      ) : null}
    </>
  );
}

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
    </svg>
  );
}
